import { useProduct, ProductProvider } from '@workspace/utils-react';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import type { JSX } from 'react/jsx-runtime';
import AppContainerFromProjects from '../../projects/src/components/AppContainer';
import { ModeProvider } from '@/providers/ModeProvider';
import { AuthBridgeProvider } from './providers/AuthBridgeProvider';
import type { PidgetSettings } from './helpers/PidgetSettings';
import LoginCallback from './LoginCallback';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { setApiBaseUrlOverride, setDocumentApiUrlOverride } from '../../projects/src/lib/data-service';
import { persistWidgetDeepLink, listenForEarlyNavigationRequest } from '../../projects/src/lib/deep-link-utils';

// Persist any widget deep-link params (`?page=Projects&entities=123`) to
// sessionStorage as early as possible — before the OIDC auth flow can
// strip query parameters from the URL.
persistWidgetDeepLink();

// The workspace sends a `NavigationRequest` postMessage when the user
// clicks a deep link (e.g. "View in Access Projects" in the Feed).
// WorkspaceNavigationListener only mounts after BootstrapGate finishes,
// so messages that arrive during bootstrap are lost.  This module-scope
// listener captures the target before React renders.
listenForEarlyNavigationRequest();

// Clear stale OIDC storage on initial app load (runs once)
// This ensures we don't use cached tokens from a previous user after logout/re-login
const clearStaleOidcStorageOnLoad = (() => {
  let hasCleared = false;
  return (authority: string, clientId: string) => {
    if (!hasCleared) {
      const oidcKey = `oidc.user:${authority}:${clientId}`;
      sessionStorage.removeItem(oidcKey);
      hasCleared = true;
    }
  };
})();

// Best practice: clean up URL after successful sign-in (removes code, state params)
const onSigninCallback = (_user: unknown): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

export const AppContainer = (): React.ReactElement => {
  const queryClient = new QueryClient();
  const product = useProduct<PidgetSettings>();

  // Set the API base URL from PidgetSettings before any queries can fire
  // This ensures all API calls use the correct URL from the workspace configuration
  if (product?.settings?.apiUrl) {
    setApiBaseUrlOverride(product.settings.apiUrl);
  }

  // Set the Document API (ADM) base URL from PidgetSettings
  if (product?.settings?.documentApiUrl) {
    setDocumentApiUrlOverride(product.settings.documentApiUrl);
  }

  if (product?.settings?.identityUrl) {
    // Clear stale OIDC storage once on app load to handle logout/re-login with different user
    clearStaleOidcStorageOnLoad(product.settings.identityUrl, product.settings.clientId);

    return (
      <QueryClientProvider client={queryClient}>
        <ProductProvider iframePidget={product}>
          <AuthProvider
            client_id={product.settings.clientId}
            redirect_uri={window.location.origin}
            silent_redirect_uri={`${window.location.origin}/login/callback`}
            scope={product.settings.scope}
            authority={product.settings.identityUrl}
            onSigninCallback={onSigninCallback}
            automaticSilentRenew={true}
            accessTokenExpiringNotificationTimeInSeconds={60}
          >
            <AuthBridgeProvider instanceId={product.instanceId}>
              <ModeProvider>
                <BrowserRouter>
                  <AppContainerFromProjects settings={product.settings} />
                </BrowserRouter>
              </ModeProvider>
            </AuthBridgeProvider>
          </AuthProvider>
        </ProductProvider>
      </QueryClientProvider>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login/callback" element={<LoginCallback />} />
      </Routes>
    </BrowserRouter>
  );
};
